import React, { useRef, useState } from "react";
import { Button, Dropdown, Menu, Tooltip } from "antd";
import { useDispatch } from "react-redux";
import useBulkImport from "../../../hooks/useBulkImport";
import { downloadImportTemplate } from "../../../util/downloadImportTemplate";
import ImportProgressModal from "./ImportProgressModal";
import ImportHistoryModal from "./ImportHistoryModal";

/**
 * "Import" toolbar control - a Dropdown (Import CSV / Import History /
 * Download Sample) + hidden file input + progress/history modals, wired to
 * useBulkImport. Clone of EmployeeInout.js's Import CSV menu, generalized so
 * Separation, Expense and Shift Change admin screens can each drop it in
 * with just their own module's endpoints (see service/index.js's
 * `<module>Import*` constants) instead of re-forking the whole flow.
 *
 * @param {Object} props
 * @param {string} props.moduleLabel - e.g. "Separation" - used in modal titles
 * @param {Object} props.endpoints - { upload, status, history, downloadErrors, undo, cancel, template }
 * @param {string} [props.sampleFileName] - fallback filename for the sample download
 * @param {Function} [props.onImportComplete] - called once a completed import should refresh the list
 * @param {string} [props.accept] - file input accept attribute
 */
const BulkImportMenu = ({
  moduleLabel,
  endpoints,
  sampleFileName = "import_sample_template.xlsx",
  onImportComplete,
  accept = ".csv,.xls,.xlsx",
}) => {
  const dispatch = useDispatch();
  const importRef = useRef(null);
  const [importInputKey, setImportInputKey] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);

  const {
    importProgressVisible,
    setImportProgressVisible,
    importStatus,
    importPolling,
    importHistoryVisible,
    setImportHistoryVisible,
    uploadFile,
    downloadErrors,
    cancelImport,
  } = useBulkImport(endpoints, onImportComplete, dispatch);

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    await uploadFile(file);
    setImportInputKey((k) => k + 1);
  };

  const menu = (
    <Menu
      onClick={() => setMenuOpen(false)}
      items={[
        {
          key: "importCsv",
          label: "Import CSV",
          onClick: () => importRef.current?.click(),
        },
        {
          key: "importHistory",
          label: "Import History",
          onClick: () => setImportHistoryVisible(true),
        },
        {
          key: "sampleCsv",
          label: "Download Sample CSV",
          onClick: () =>
            downloadImportTemplate(endpoints.template, { fallbackName: sampleFileName }),
        },
      ]}
    />
  );

  return (
    <>
      <Tooltip title="Please check column headers in your CSV file match the Sample CSV exactly.">
        <Dropdown
          overlay={menu}
          placement="bottomRight"
          open={menuOpen}
          onOpenChange={setMenuOpen}
        >
          <Button className="btn-secondary">Import</Button>
        </Dropdown>
      </Tooltip>

      <input
        key={importInputKey}
        type="file"
        accept={accept}
        style={{ display: "none" }}
        ref={importRef}
        onChange={handleFileChange}
      />

      <ImportProgressModal
        title={`Importing ${moduleLabel}`}
        visible={importProgressVisible}
        importStatus={importStatus}
        importPolling={importPolling}
        onClose={() => setImportProgressVisible(false)}
        onCancelImport={cancelImport}
        onDownloadErrors={downloadErrors}
      />

      <ImportHistoryModal
        visible={importHistoryVisible}
        onClose={() => setImportHistoryVisible(false)}
        title={`${moduleLabel} Import History`}
        historyUrl={endpoints.history}
        downloadErrorsUrl={endpoints.downloadErrors}
        undoUrl={endpoints.undo}
        onUndo={onImportComplete}
      />
    </>
  );
};

export default BulkImportMenu;
